import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BookMarked, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { notificar } from '@/shared/ui/avisos';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Interruptor } from '@/shared/ui/Interruptor';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDeLaEntrada } from './PanelDeLaEntrada';
import { mensajeDe } from './mensajes';
import type { CatalogoTributario, EntradaDeCatalogo } from './contrato';

export function PaginaCatalogosTributarios() {
  const [cual, setCual] = useState('sustento');
  const [editando, setEditando] = useState<EntradaDeCatalogo | null>(null);
  const [creando, setCreando] = useState(false);
  const [aBorrar, setABorrar] = useState<EntradaDeCatalogo | null>(null);

  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('compras.gestionar');

  const cuales = useQuery({
    queryKey: ['compras', 'catalogos', 'cuales'],
    queryFn: () => api.get<CatalogoTributario[]>('/compras/catalogos'),
    staleTime: Infinity,
  });

  const entradas = useQuery({
    queryKey: ['compras', 'catalogos', cual],
    queryFn: () => api.get<EntradaDeCatalogo[]>(`/compras/catalogos/${cual}`),
  });

  const ofrecer = useMutation({
    mutationFn: (e: EntradaDeCatalogo) =>
      api.put<EntradaDeCatalogo>(`/compras/catalogos/entradas/${e.id}`, {
        codigo: e.codigo,
        etiqueta: e.etiqueta,
        orden: e.orden,
        activo: !e.activo,
        nota: e.nota ?? null,
      }),
    onSuccess: (e) => {
      notificar.exito(e.activo ? 'Se ofrece' : 'Ya no se ofrece');
      refrescar();
    },
    onError: (causa) => notificar.error(mensajeDe(causa, 'No se pudo cambiar')),
  });

  const borrar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/compras/catalogos/entradas/${id}`),
    onSuccess: () => {
      notificar.exito('Entrada borrada');
      setABorrar(null);
      refrescar();
    },
    onError: (causa) => notificar.error(mensajeDe(causa, 'No se pudo borrar')),
  });

  function refrescar() {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['compras'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['proveedores'] });
  }

  const elegido = cuales.data?.find((c) => c.clave === cual);

  return (
    <>
      <EncabezadoDePagina
        icono={BookMarked}
        color="indigo"
        titulo="Catálogos tributarios"
        subtitulo="Los vocabularios del SRI · se editan sin desplegar"
        acciones={
          puedeGestionar &&
          elegido?.admiteNuevos && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-4" aria-hidden />
              Nueva entrada
            </button>
          )
        }
      />

      <nav
        aria-label="Qué vocabulario"
        className="mb-6 flex gap-1 overflow-x-auto border-b border-armazon-200"
      >
        {(cuales.data ?? []).map((c) => (
          <button
            key={c.clave}
            type="button"
            onClick={() => setCual(c.clave)}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-[13px] font-semibold uppercase tracking-wide transition-colors ${
              c.clave === cual
                ? 'border-marca-600 text-armazon-900'
                : 'border-transparent text-armazon-400 hover:text-armazon-600'
            }`}
          >
            {c.etiqueta}
            {!c.admiteNuevos && <Lock className="size-3.5" aria-hidden />}
          </button>
        ))}
      </nav>

      {elegido && !elegido.admiteNuevos && (
        <p className="mb-4 flex gap-3 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
          <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Aquí no se crean códigos nuevos: cada uno lleva un comportamiento que el sistema
            tiene que saber. Sí se cambia cómo se llaman, en qué orden salen y cuáles se ofrecen.
          </span>
        </p>
      )}

      {entradas.isPending ? (
        <Cargando titulo="Cargando" detalle="leyendo el catálogo" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {['Código', 'Cómo se llama', 'Orden', 'Se ofrece', ''].map((c, i) => (
                  <th
                    key={c || 'acciones'}
                    scope="col"
                    className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                      i === 2 || i === 3 ? 'text-center' : i === 4 ? 'text-right' : 'text-left'
                    }`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {(entradas.data ?? []).map((e) => (
                <tr key={e.id} className="transition-colors hover:bg-armazon-50/60">
                  <td className="px-4 py-2.5">
                    <span className="importe text-xs font-semibold text-armazon-800">
                      {e.codigo}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="block text-xs uppercase text-armazon-800">{e.etiqueta}</span>
                    {e.nota && (
                      <span className="block text-[10px] uppercase text-armazon-400">{e.nota}</span>
                    )}

                    {!e.laEntiendeElSistema && (
                      <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700">
                        <AlertTriangle className="size-3" aria-hidden />
                        El sistema no sabe tratar este código
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center text-xs tabular-nums text-armazon-500">
                    {e.orden}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="flex justify-center">
                      <Interruptor
                        activo={e.activo}
                        alCambiar={() => ofrecer.mutate(e)}
                        etiqueta={`Ofrecer ${e.codigo} · ${e.etiqueta}`}
                        deshabilitado={!puedeGestionar || ofrecer.isPending}
                      />
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {puedeGestionar && (
                      <span className="flex justify-end gap-1">
                        <BotonDeFila
                          icono={Pencil}
                          titulo="Editar"
                          alPulsar={() => setEditando(e)}
                        />

                        {!e.delSistema && (
                          <BotonDeFila
                            icono={Trash2}
                            titulo="Borrar"
                            alPulsar={() => setABorrar(e)}
                          />
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creando || editando) && (
        <PanelDeLaEntrada
          catalogo={cual}
          catalogoEtiqueta={elegido?.etiqueta ?? ''}
          entrada={editando}
          alCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
          alGuardar={() => {
            setCreando(false);
            setEditando(null);
            refrescar();
          }}
        />
      )}

      <ConfirmarEliminacion
        abierto={aBorrar !== null}
        titulo="Borrar la entrada"
        descripcion={
          aBorrar
            ? `«${aBorrar.codigo} · ${aBorrar.etiqueta}» dejará de existir. Los comprobantes que ya la usen seguirán guardando su código.`
            : ''
        }
        alCerrar={() => setABorrar(null)}
        alConfirmar={() => aBorrar && borrar.mutate(aBorrar.id)}
        eliminando={borrar.isPending}
      />
    </>
  );
}
